/* ccs-plan-brain — keeps every subscribed cart line on the correct selling plan.
 *
 * Rules (spec: vault _context/specs/2026-06-10-subscription-4pct-spec.md §12, Akash-signed):
 *   · per-line quantity tier → that tier's baked plan (5/8/10/12%)
 *   · ≥5 DISTINCT subscribed products → stacked tier+4% plan (8.8/11.68/13.6%) or plain 4%
 *   · floor rule: if the stacked group doesn't exist on the product, stay tier-only
 *   · one-time lines are never touched; cadence is preserved on every swap
 *
 * Plans are matched by their percentage (price_adjustments[0].value from
 * /products/{handle}.js) — id-format-proof and survives plan re-creation.
 * Only lines that ALREADY carry a selling plan are managed; the PDP picker
 * (ccs-plan-picker.liquid) is the only entry into auto-ship.
 */
(function () {
  'use strict';

  // Subscriptions use a clean standard ladder (5/8/10/12%) — each product's
  // per-SKU tier rounds DOWN to the nearest standard rung (margin-safe), then
  // the 4% stack compounds at 5+ distinct products. These are the only 9 plan
  // groups that exist; one-time orders still get the exact per-SKU tier via
  // the cart function.
  function norm(p) { return String(parseFloat(p)); }
  var STD = [12, 10, 8, 5];
  var STACK = { '5': '8.8', '8': '11.68', '10': '13.6', '12': null }; // 12 has no floor-safe stack
  function roundDownStd(pct) {
    for (var i = 0; i < STD.length; i++) if (pct >= STD[i]) return STD[i];
    return 0;
  }
  function targetKey(tierPct, stacked) {
    var t = roundDownStd(tierPct);
    if (!t) return stacked ? '4' : '0';
    if (!stacked) return norm(t);
    return STACK[norm(t)] || norm(t); // 12 stays 12 (no stack)
  }

  var productCache = {}; // handle → Promise<{plans: {pctKey: {monthly, bimonthly}}, slotOf: {planId: 'monthly'|'bimonthly'}}>
  function productPlans(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var out = { plans: {}, slotOf: {} };
          if (!p || !p.selling_plan_groups) return out;
          p.selling_plan_groups.forEach(function (g) {
            (g.selling_plans || []).forEach(function (sp) {
              var adj = sp.price_adjustments && sp.price_adjustments[0];
              var pct = adj && adj.value_type === 'percentage' ? norm(adj.value) : '0';
              var slot = /2\s*month/i.test(sp.name || '') ? 'bimonthly' : 'monthly';
              out.plans[pct] = out.plans[pct] || {};
              out.plans[pct][slot] = sp.id;
              out.slotOf[sp.id] = slot;
            });
          });
          return out;
        })
        .catch(function () { return { plans: {}, slotOf: {} }; });
    }
    return productCache[handle];
  }

  function tierPctFor(item) {
    var ladder = window.__ccsTiers && window.__ccsTiers[item.variant_id];
    if (ladder === false) return 0;          // confirmed not tier-eligible
    if (!Array.isArray(ladder)) return null; // unknown — leave the line alone
    var pct = 0;
    ladder.forEach(function (t) {
      var p = t && (t.discount_pct || t.pct) || 0;
      if (t && item.quantity >= t.min_qty && p > pct) pct = p;
    });
    return pct;
  }

  var busy = false, rerun = false;

  function evaluate(cart) {
    if (!cart || !cart.items) return;
    var subbed = cart.items.filter(function (i) {
      return i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
    });
    updateNudge(subbed);
    if (!subbed.length) return;
    if (busy) { rerun = true; return; }
    busy = true;

    Promise.all(subbed.map(function (i) { return productPlans(i.handle); })).then(function (maps) {
      var changes = [];
      var stacked = distinctCount(subbed) >= 5;
      subbed.forEach(function (item, idx) {
        var map = maps[idx];
        var current = item.selling_plan_allocation.selling_plan.id;
        var slot = map.slotOf[current] || 'monthly';
        var tierPct = tierPctFor(item);
        if (tierPct === null) return; // ladder unknown — leave the line alone
        // candidate pct keys in priority order; floor rule = tier-only before losing the tier
        var cands = [];
        if (stacked) cands.push(targetKey(tierPct, true));
        cands.push(targetKey(tierPct, false));
        if (stacked) cands.push('4');
        cands.push('0');
        for (var c = 0; c < cands.length; c++) {
          var key = cands[c];
          if (key !== null && map.plans[key] && map.plans[key][slot]) {
            var target = map.plans[key][slot];
            if (target !== current) changes.push({ key: item.key, quantity: item.quantity, selling_plan: target });
            break;
          }
        }
      });
      if (!changes.length) { busy = false; flushRerun(); return; }
      applySequentially(changes, 0);
    }).catch(function () { busy = false; });
  }

  function distinctCount(subbed) {
    var seen = {};
    subbed.forEach(function (i) { seen[i.product_id] = true; });
    return Object.keys(seen).length;
  }

  // Keys regenerate after every change — re-resolve each next line from a fresh /cart.js.
  function applySequentially(changes, i) {
    if (i >= changes.length) {
      busy = false;
      if (window.ccsCartDrawerRefresh) window.ccsCartDrawerRefresh();
      flushRerun();
      return;
    }
    var ch = changes[i];
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: ch.key, quantity: ch.quantity, selling_plan: ch.selling_plan })
    }).then(function (r) {
      if (r.status === 429) { // bot-protection pacing — back off once, then continue
        return new Promise(function (res) { setTimeout(res, 2600); }).then(function () { return null; });
      }
      return r.json().catch(function () { return null; });
    }).then(function (freshCart) {
      var next = i + 1;
      if (next >= changes.length) { applySequentially(changes, next); return; }
      var go = function (cartJson) {
        if (cartJson && cartJson.items) {
          // re-map remaining changes onto fresh keys by variant + plan-mismatch
          for (var j = next; j < changes.length; j++) {
            var want = changes[j];
            var hit = cartJson.items.filter(function (it) {
              return it.selling_plan_allocation && it.selling_plan_allocation.selling_plan &&
                it.selling_plan_allocation.selling_plan.id !== want.selling_plan &&
                it.key.indexOf(String(want.key).split(':')[0]) === 0;
            })[0];
            if (hit) { want.key = hit.key; want.quantity = hit.quantity; }
          }
        }
        setTimeout(function () { applySequentially(changes, next); }, 700);
      };
      if (freshCart && freshCart.items) go(freshCart);
      else fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }).then(go).catch(function () { go(null); });
    }).catch(function () { busy = false; });
  }

  function flushRerun() {
    if (!rerun) return;
    rerun = false;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(evaluate)
      .catch(function () {});
  }

  // ── drawer nudge strip ─────────────────────────────────────────────
  function updateNudge(subbed) {
    var el = document.getElementById('ccs-cd-as-nudge');
    if (!el) return;
    if (!subbed || !subbed.length) { el.style.display = 'none'; return; }
    var n = distinctCount(subbed);
    el.style.display = '';
    if (n >= 5) {
      el.classList.add('ccs-cd-as-nudge-on');
      el.textContent = '✓ 4% auto-ship savings applied to your subscription';
    } else {
      el.classList.remove('ccs-cd-as-nudge-on');
      var left = 5 - n;
      el.textContent = 'Auto-ship ' + left + ' more product' + (left === 1 ? '' : 's') + ' to save 4% on your whole auto-ship';
    }
  }

  document.addEventListener('ccs:cart:updated', function (e) {
    if (e.detail && e.detail.items) evaluate(e.detail);
  });
  // first paint (drawer may not have fired yet)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flushRerunInit);
  } else { flushRerunInit(); }
  function flushRerunInit() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(evaluate)
      .catch(function () {});
  }
})();
